import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import {
  Alert,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  CircularProgress,
  FormControlLabel,
  IconButton,
  InputAdornment,
  Link,
  List,
  ListItem,
  ListItemButton,
  ListItemText,
  TextField,
  Typography,
  useTheme,
} from '@mui/material'
import { alpha } from '@mui/material/styles'
import VisibilityIcon from '@mui/icons-material/Visibility'
import VisibilityOffIcon from '@mui/icons-material/VisibilityOff'
import { fetchSearchEmployers, fetchEmployerUserSignupRegister } from 'src/redux/employerUserSignup'
import { fetchTrackEvent, ProductAnalyticsEventTypes } from 'src/redux/productAnalytics'

const PASSWORD_RULES = [
  { label: 'At least 8 characters', test: v => v.length >= 8 },
  { label: 'At least one uppercase letter', test: v => /[A-Z]/.test(v) },
  { label: 'At least one lowercase letter', test: v => /[a-z]/.test(v) },
  { label: 'At least one number', test: v => /[0-9]/.test(v) },
  { label: 'At least one special character', test: v => /[^A-Za-z0-9]/.test(v) },
]

const isPasswordValid = password => PASSWORD_RULES.every(rule => rule.test(password))

const EMPLOYER_SEARCH_DEBOUNCE_MS = 300

const EmployerUserAccountSetupStep = ({ wizardData, setWizardData, onNext, onBack }) => {
  const dispatch = useDispatch()
  const theme = useTheme()
  const { searchingEmployers, employerSearchResults, registering, registerResult } = useSelector(
    state => state.employerUserSignup,
  )

  const [showPassword, setShowPassword] = useState(false)
  const [showConfirmPassword, setShowConfirmPassword] = useState(false)
  const [confirmPassword, setConfirmPassword] = useState('')
  const [agreedToTerms, setAgreedToTerms] = useState(false)
  const [touched, setTouched] = useState({})
  const [showNewEmployerConfirmation, setShowNewEmployerConfirmation] = useState(false)

  const hasSubmittedRef = useRef(false)
  const hasTrackedFormStartRef = useRef(false)
  const searchDebounceRef = useRef(null)

  const trackFormStart = () => {
    if (!hasTrackedFormStartRef.current) {
      hasTrackedFormStartRef.current = true
      dispatch(
        fetchTrackEvent({
          eventName: 'signup_account_form_started',
          eventType: ProductAnalyticsEventTypes.ACTION,
          distinctId: wizardData.email,
        }),
      )
    }
  }

  useEffect(() => {
    if (hasSubmittedRef.current && !registering && registerResult) {
      dispatch(
        fetchTrackEvent({
          eventName: 'signup_account_created',
          eventType: ProductAnalyticsEventTypes.ACTION,
          distinctId: wizardData.email,
          eventData: {
            isNewEmployer: !wizardData.employerId,
            employerId: registerResult.employerId,
          },
        }),
      )
      setWizardData(prev => ({
        ...prev,
        userId: registerResult.userId,
        employerId: registerResult.employerId,
        employerUserId: registerResult.employerUserId,
        confirmationPollingToken: registerResult.confirmationPollingToken,
        employerHasActiveSubscription: registerResult.employerHasActiveSubscription,
        registrationStatus: registerResult.registrationStatus,
        isConsumerEmail: registerResult.isConsumerEmail,
        isNewEmployer: registerResult.isNewEmployer,
      }))
      onNext()
    }
  }, [registering, registerResult, setWizardData, onNext, dispatch, wizardData.email, wizardData.employerId])

  const employerOptions = employerSearchResults.map(employer => ({
    id: employer.id,
    name: employer.name,
  }))

  const selectedOption = wizardData.employerId
    ? employerOptions.find(opt => opt.id === wizardData.employerId) || null
    : null

  const handleFieldChange = (name, value) => {
    setWizardData(prev => ({ ...prev, [name]: value }))
  }

  const handleBlur = name => {
    setTouched(prev => ({ ...prev, [name]: true }))
  }

  const handleEmployerChange = (event, value) => {
    setShowNewEmployerConfirmation(false)
    if (value && typeof value === 'object') {
      setWizardData(prev => ({ ...prev, employerId: value.id, employerName: value.name }))
    } else if (typeof value === 'string') {
      setWizardData(prev => ({ ...prev, employerId: null, employerName: value }))
    } else {
      setWizardData(prev => ({ ...prev, employerId: null, employerName: '' }))
    }
  }

  const handleEmployerInputChange = useCallback(
    (event, value, reason) => {
      if (reason === 'input') {
        trackFormStart()
        setShowNewEmployerConfirmation(false)
        setWizardData(prev => ({ ...prev, employerId: null, employerName: value }))

        clearTimeout(searchDebounceRef.current)
        if (value.trim().length >= 2) {
          searchDebounceRef.current = setTimeout(() => {
            dispatch(fetchSearchEmployers(value))
            dispatch(
              fetchTrackEvent({
                eventName: 'signup_employer_searched',
                eventType: ProductAnalyticsEventTypes.ACTION,
                distinctId: wizardData.email,
                eventData: { searchTerm: value },
              }),
            )
          }, EMPLOYER_SEARCH_DEBOUNCE_MS)
        }
      }
    },
    [dispatch, setWizardData, wizardData.email],
  )

  const passwordMismatch =
    touched.confirmPassword &&
    confirmPassword.length > 0 &&
    wizardData.password !== confirmPassword

  const isFormValid =
    (wizardData.firstName || '').trim().length > 0 &&
    (wizardData.lastName || '').trim().length > 0 &&
    (wizardData.employerName || '').trim().length > 0 &&
    isPasswordValid(wizardData.password || '') &&
    wizardData.password === confirmPassword &&
    agreedToTerms

  const dispatchRegistration = () => {
    hasSubmittedRef.current = true
    dispatch(
      fetchEmployerUserSignupRegister({
        email: wizardData.email,
        password: wizardData.password,
        firstName: wizardData.firstName,
        lastName: wizardData.lastName,
        jobTitle: wizardData.jobTitle,
        employerName: wizardData.employerName,
        employerId: wizardData.employerId,
      }),
    )
  }

  const handleSubmit = () => {
    setTouched({
      firstName: true,
      lastName: true,
      employerName: true,
      password: true,
      confirmPassword: true,
    })
    if (!isFormValid) {
      const missingFields = []
      if (!(wizardData.firstName || '').trim()) missingFields.push('firstName')
      if (!(wizardData.lastName || '').trim()) missingFields.push('lastName')
      if (!(wizardData.employerName || '').trim()) missingFields.push('employerName')
      if (!isPasswordValid(wizardData.password || '')) missingFields.push('password')
      if (wizardData.password !== confirmPassword) missingFields.push('confirmPassword')
      if (!agreedToTerms) missingFields.push('terms')
      dispatch(
        fetchTrackEvent({
          eventName: 'signup_account_validation_error',
          eventType: ProductAnalyticsEventTypes.ACTION,
          distinctId: wizardData.email,
          eventData: { missingFields },
        }),
      )
      return
    }

    if (wizardData.isClaimingExistingAccount && wizardData.employerId) {
      dispatchRegistration()
      return
    }

    if (!wizardData.employerId) {
      dispatch(fetchSearchEmployers(wizardData.employerName))
      setShowNewEmployerConfirmation(true)
      return
    }

    dispatchRegistration()
  }

  const handleSelectSuggestion = employer => {
    setWizardData(prev => ({ ...prev, employerId: employer.id, employerName: employer.name }))
    setShowNewEmployerConfirmation(false)
  }

  const handleConfirmNewEmployer = () => {
    dispatch(
      fetchTrackEvent({
        eventName: 'signup_new_employer_confirmed',
        eventType: ProductAnalyticsEventTypes.ACTION,
        distinctId: wizardData.email,
        eventData: { employerName: wizardData.employerName },
      }),
    )
    setShowNewEmployerConfirmation(false)
    dispatchRegistration()
  }

  return (
    <Box>
      <Typography variant='h5' gutterBottom>
        Set up your account
      </Typography>
      <Typography variant='body1' color='text.secondary' sx={{ mb: 3 }}>
        Create your free account to get started.
      </Typography>

      {wizardData.isClaimingExistingAccount && wizardData.employerId && (
        <Alert severity='info' sx={{ mb: 2, borderRadius: '8px' }}>
          <Typography variant='body2'>
            We may already have jobs posted on behalf of <strong>{wizardData.employerName}</strong>. Set up your account to get started.
          </Typography>
        </Alert>
      )}

      <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
        <TextField
          label='First Name'
          value={wizardData.firstName || ''}
          required
          onFocus={trackFormStart}
          onChange={e => handleFieldChange('firstName', e.target.value)}
          onBlur={() => handleBlur('firstName')}
          fullWidth
          error={touched.firstName && !(wizardData.firstName || '').trim()}
          helperText={touched.firstName && !(wizardData.firstName || '').trim() ? 'First name is required.' : ''}
        />

        <TextField
          label='Last Name'
          value={wizardData.lastName || ''}
          required
          onChange={e => handleFieldChange('lastName', e.target.value)}
          onBlur={() => handleBlur('lastName')}
          fullWidth
          error={touched.lastName && !(wizardData.lastName || '').trim()}
          helperText={touched.lastName && !(wizardData.lastName || '').trim() ? 'Last name is required.' : ''}
        />

        <TextField
          label='Job Title (Optional)'
          value={wizardData.jobTitle || ''}
          onChange={e => handleFieldChange('jobTitle', e.target.value)}
          fullWidth
        />

        {wizardData.isClaimingExistingAccount && wizardData.employerId ? (
          <TextField
            label='Organization'
            value={wizardData.employerName || ''}
            fullWidth
            disabled
          />
        ) : (
          <Autocomplete
            freeSolo
            forcePopupIcon
            options={employerOptions}
            getOptionLabel={option => (typeof option === 'string' ? option : option.name)}
            value={selectedOption}
            inputValue={wizardData.employerName || ''}
            onChange={handleEmployerChange}
            onInputChange={handleEmployerInputChange}
            loading={searchingEmployers}
            renderInput={params => (
              <TextField
                {...params}
                label='Organization'
                placeholder='Search or type your organization name'
                autoComplete='organization'
                required
                fullWidth
                onBlur={() => handleBlur('employerName')}
                error={touched.employerName && !(wizardData.employerName || '').trim()}
                helperText={
                  touched.employerName && !(wizardData.employerName || '').trim()
                    ? 'Organization name is required.'
                    : ''
                }
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {searchingEmployers ? <CircularProgress size={16} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
          />
        )}

        {wizardData.employerId && !wizardData.isClaimingExistingAccount && (
          <Alert severity='success' sx={{ borderRadius: '8px' }}>
            <Typography variant='body2'>
              We found <strong>{wizardData.employerName}</strong> in our system.
            </Typography>
          </Alert>
        )}

        {showNewEmployerConfirmation && !wizardData.employerId && (
          <Box
            sx={{
              border: `1px solid ${theme.palette.info.light}`,
              borderRadius: '8px',
              p: 2.5,
              backgroundColor: alpha(theme.palette.info.light, 0.04),
            }}
          >
            <Typography variant='body2' sx={{ mb: 2 }}>
              We don't have "<strong>{wizardData.employerName}</strong>" in our system yet.
              {employerSearchResults.length > 0
                ? ' Did you mean one of these?'
                : ' Please confirm below to create it as a new organization.'}
            </Typography>

            {searchingEmployers && (
              <Box sx={{ display: 'flex', justifyContent: 'center', py: 2 }}>
                <CircularProgress size={24} />
              </Box>
            )}

            {!searchingEmployers && employerSearchResults.length > 0 && (
              <Box sx={{ mb: 2 }}>
                <List
                  dense
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '8px',
                    overflow: 'hidden',
                    backgroundColor: theme.palette.common.white,
                  }}
                >
                  {employerSearchResults.map(employer => (
                    <ListItem key={employer.id} disablePadding divider>
                      <ListItemButton onClick={() => handleSelectSuggestion(employer)}>
                        <ListItemText primary={employer.name} />
                        <Typography variant='body2' color='primary' sx={{ fontWeight: 600, ml: 2 }}>
                          Select
                        </Typography>
                      </ListItemButton>
                    </ListItem>
                  ))}
                </List>
              </Box>
            )}

            {!searchingEmployers && (
              <Button
                variant='contained'
                fullWidth
                onClick={handleConfirmNewEmployer}
                disabled={registering}
                startIcon={registering ? <CircularProgress size={16} color='inherit' /> : null}
              >
                {registering
                  ? 'Creating account...'
                  : employerSearchResults.length > 0
                    ? `None of these \u2014 create "${wizardData.employerName}" as a new organization`
                    : `Confirm \u2014 create "${wizardData.employerName}" as a new organization`}
              </Button>
            )}
          </Box>
        )}

        <TextField
          label='Password'
          type={showPassword ? 'text' : 'password'}
          value={wizardData.password || ''}
          required
          onChange={e => handleFieldChange('password', e.target.value)}
          onBlur={() => handleBlur('password')}
          fullWidth
          error={touched.password && !isPasswordValid(wizardData.password || '')}
          helperText={
            touched.password && !isPasswordValid(wizardData.password || '')
              ? 'Password must be 8+ characters with uppercase, lowercase, number, and special character.'
              : 'Must be 8+ characters with uppercase, lowercase, number, and special character.'
          }
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={() => setShowPassword(prev => !prev)} edge='end'>
                  {showPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <TextField
          label='Confirm Password'
          type={showConfirmPassword ? 'text' : 'password'}
          value={confirmPassword}
          onChange={e => setConfirmPassword(e.target.value)}
          onBlur={() => handleBlur('confirmPassword')}
          fullWidth
          error={passwordMismatch}
          helperText={passwordMismatch ? 'Passwords do not match.' : ''}
          InputProps={{
            endAdornment: (
              <InputAdornment position='end'>
                <IconButton onClick={() => setShowConfirmPassword(prev => !prev)} edge='end'>
                  {showConfirmPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>
              </InputAdornment>
            ),
          }}
        />

        <FormControlLabel
          control={
            <Checkbox
              checked={agreedToTerms}
              onChange={e => setAgreedToTerms(e.target.checked)}
            />
          }
          label={
            <Typography variant='body2'>
              I agree to the{' '}
              <Link href='/tac' target='_blank' rel='noopener noreferrer' underline='always'>
                Terms of Service
              </Link>
              ,{' '}
              <Link href='/privacy' target='_blank' rel='noopener noreferrer' underline='always'>
                Privacy Policy
              </Link>
              , and{' '}
              <Link href='/employer-terms' target='_blank' rel='noopener noreferrer' underline='always'>
                Employer Service Agreement
              </Link>
            </Typography>
          }
          sx={{ mt: 1 }}
        />

      </Box>

      <Box sx={{ display: 'flex', justifyContent: 'space-between', mt: 4 }}>
        <Button onClick={onBack} disabled={registering}>
          Back
        </Button>
        {!showNewEmployerConfirmation && (
          <Button
            variant='contained'
            onClick={handleSubmit}
            disabled={!isFormValid || registering}
            startIcon={registering ? <CircularProgress size={16} color='inherit' /> : null}
          >
            {registering ? 'Creating account...' : 'Continue'}
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default EmployerUserAccountSetupStep
